import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges
} from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { AddressFieldsetComponentDef } from '@df/ui/form/address-fieldset.component.def';
import { AddressSearcherService } from '@df/ui/form/address-searcher.service';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import find from 'lodash-es/find';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';

export const NAME_PREFIXES = ['Mr', 'Mrs', 'Miss'];

export class AddressStatusChangeEvent {
    constructor(
        public valid: boolean,
        public data: IAddressData,
        public isNew?: boolean
    ) {}
}

@Directive()
export abstract class LocalAddressFieldsetComponentDef extends AddressFieldsetComponentDef implements OnInit, OnChanges, OnDestroy {
    _canShowForm = false;

    prefixes = NAME_PREFIXES;

    protected formSubmittedSubscription?: Subscription;

    @Input()
    submitSubject?: Subject<boolean>;

    @Input()
    isBilling?: boolean;

    @Output()
    inputsBlur = new EventEmitter();

    @Output()
    addressStatusChange = new EventEmitter<AddressStatusChangeEvent>();

    set canShowForm(value: boolean) {
        this._canShowForm = value;
        this.detectChanges();
    }

    get canShowForm() {
        return this._canShowForm;
    }

    constructor(
        @Optional() protected override addressSearcher: AddressSearcherService,
        override readonly app: App,
        protected override changeDetectorRef: ChangeDetectorRef,
        protected override timeoutService: TimeoutService,
        dfApiConfigService: DfApiConfigService,
        protected elementRef: ElementRef,
        protected toggleService: ToggleService
    ) {
        super(addressSearcher, app, changeDetectorRef, timeoutService, dfApiConfigService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.data && this.data.street_1) {
            this.canShowForm = true;
        }
    }

    override ngOnChanges(changesObj: SimpleChanges) {
        if (changesObj['submitSubject']) {
            this.clearSubmitSubscription();
            if (this.submitSubject) {
                this.formSubmittedSubscription = this.submitSubject.subscribe((value: boolean) => {
                    if (value && !this.addressIsValid) {
                        this.canShowForm = true;
                    }
                });
            }
        }
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.clearSubmitSubscription();
    }

    protected clearSubmitSubscription(): void {
        if (this.formSubmittedSubscription) {
            this.formSubmittedSubscription.unsubscribe();
        }
    }

    protected onFormSubmitted(): void {}

    onInputBlur(): void {
        this.inputsBlur.emit();
    }

    get addressIsValid(): boolean {
        const address = this.data;

        if (address) {
            return !!(
                address.firstname &&
                address.lastname &&
                address.telephone &&
                address.country_id &&
                address.postcode &&
                address.street_1 &&
                address.city &&
                (!this.regionRequired || address.region || address.region_id)
            );
        }

        return false;
    }

    override onSearcherSelectionApplied(): void {
        super.onSearcherSelectionApplied();
        this.canShowForm = true;

        this.addressStatusChange.next(new AddressStatusChangeEvent(this.addressIsValid, this.data, true));

        // find first invalid input and focus it
        const invalidElement = find(this.elementRef.nativeElement.querySelectorAll('.ng-invalid'), i =>
            ['input', 'select', 'textarea'].includes(i.tagName.toLowerCase())
        );

        invalidElement?.focus();
    }
}
